// src/features/patient/care-treatment/utils/careTimeline.ts

/**
 * Purpose : Everything the Care & Treatment screens work out from
 * encounters and notes: labels, filters, dates, counts, which notes belong to
 * which visit. Pure functions, no React.
 *
 * Contents :
 *
 * * const(CLASS_META) / function(classMeta) / const(SOURCE_KIND_LABEL)
 * * function(visitTitle) / function(noteTypeLabel) / function(pageRangeText)
 * * function(formatWhen) / function(formatDateOnly) / function(formatLongDate) / function(formatTime)
 * * function(sortNewestFirst) / function(nonVisits)
 * * function(noteIndex) / function(notesForVisit) : notes by visit, built once per notes list
 * * function(isOrderNote) / function(noteChips) / function(isInferredVisit)
 * * function(notesToReview) / function(notesWithoutVisit) / function(summaryCounts)
 * * function(searchTerms) : the words of a search, to mark in a note
 */

import type { Encounter, EncounterClass, Note, Source, SourceKind } from "@/features/patient/care-treatment/api/careTimelineTypes";

// const(CLASS_META)
export const CLASS_META: Record<EncounterClass, { label: string; icon: string }> = {
  office: { label: "Office visit", icon: "lucide:stethoscope" },
  hospital: { label: "Hospital stay", icon: "lucide:building-2" },
  procedure: { label: "Procedure", icon: "lucide:activity" },
  imaging: { label: "Imaging", icon: "lucide:scan-line" },
  emergency: { label: "Emergency", icon: "lucide:ambulance" },
  telehealth: { label: "Telehealth", icon: "lucide:video" },
  phone_call: { label: "Phone call", icon: "lucide:phone" },
  portal_message: { label: "Portal message", icon: "lucide:message-square" },
  results_review: { label: "Results reviewed", icon: "lucide:clipboard-check" },
  referral: { label: "Referral", icon: "lucide:send" },
  addendum: { label: "Addendum", icon: "lucide:file-plus" },
  letter: { label: "Letter", icon: "lucide:mail" },
  other: { label: "Other record", icon: "lucide:file-text" },
};

// function(classMeta)
/** A class's label and icon; any value the screens don't know reads as "Other record", never a crash. */
export function classMeta(encounterClass: string | undefined): { label: string; icon: string } {
  return CLASS_META[encounterClass as EncounterClass] ?? CLASS_META.other;
}

// const(SOURCE_KIND_LABEL)
export const SOURCE_KIND_LABEL: Record<SourceKind, string> = {
  epic: "Epic",
  pdf: "PDF",
  ccd: "CCD",
  athena: "athena",
};

const UNKNOWN_NOTE_TYPE = "Clinical note";

/** A note's printed type when the backend gave no display name (lower-cased key). */
const NOTE_TYPE_NAMES: Record<string, string> = {
  "progress notes": "Progress note",
  "progress note": "Progress note",
  telephone: "Telephone note",
  "telephone encounter": "Telephone note",
  "plan of care": "Plan of care",
  "lactation note": "Lactation note",
  "nursing note": "Nursing note",
  "discharge summary": "Discharge summary",
  "h&p": "History and physical",
};

// function(visitTitle)
/** The reason for the visit, else its type ("Office visit"). */
export function visitTitle(encounter: Encounter): string {
  return encounter.reason?.trim() || classMeta(encounter.class).label;
}

// function(noteTypeLabel)
/** The note's type (its title or nearest type heading), or "Clinical note" when it has no label at all. */
export function noteTypeLabel(note: Note): string {
  return note.typeDisplay.trim() || NOTE_TYPE_NAMES[note.typeRaw.trim().toLowerCase()] || UNKNOWN_NOTE_TYPE;
}

// function(pageRangeText)
/** "Page 72" / "Pages 37–40", or "" when the source has no pages. */
export function pageRangeText(source: Source): string {
  if (!source.pageStart) return "";
  if (!source.pageEnd || source.pageEnd === source.pageStart) return `Page ${source.pageStart}`;
  return `Pages ${source.pageStart}–${source.pageEnd}`;
}

/** A date-only value is a local day; anything longer is a real instant. */
function parse(value: string): Date | null {
  if (!value) return null;
  const dateOnly = /^(\d{4})-(\d{2})-(\d{2})$/.exec(value);
  const date = dateOnly ? new Date(+dateOnly[1], +dateOnly[2] - 1, +dateOnly[3]) : new Date(value);
  return Number.isNaN(date.getTime()) ? null : date;
}

const day = (d: Date) => d.toLocaleDateString("en-US", { month: "short", day: "numeric", year: "numeric" });
const time = (d: Date) => d.toLocaleTimeString("en-US", { hour: "numeric", minute: "2-digit" });
const sameDay = (a: Date, b: Date) => a.toDateString() === b.toDateString();

// function(formatDateOnly)
/** "Oct 1, 2024", or "" when there is no usable date. */
export function formatDateOnly(value: string | undefined): string {
  const d = parse(value ?? "");
  return d ? day(d) : "";
}

// function(formatWhen)
/**
 * A visit's or note's date for a card.
 *
 * @returns "Sep 9, 2025 · 1:40 PM" (time only when known), "Nov 19 – 23, 2016"
 *   for a stay over several days, or "" when there is no usable date.
 */
export function formatWhen(start: string, end: string | undefined, hasTime: boolean): string {
  const from = parse(start);
  if (!from) return "";
  const to = end ? parse(end) : null;
  if (to && !sameDay(from, to)) {
    if (from.getFullYear() === to.getFullYear() && from.getMonth() === to.getMonth()) {
      return `${from.toLocaleDateString("en-US", { month: "short" })} ${from.getDate()} – ${to.getDate()}, ${to.getFullYear()}`;
    }
    return `${day(from)} – ${day(to)}`;
  }
  return hasTime ? `${day(from)} · ${time(from)}` : day(from);
}

// function(formatLongDate)
/** "Wednesday, Nov 16, 2016", or "" when there is no usable date. */
export function formatLongDate(value: string | undefined): string {
  const d = parse(value ?? "");
  return d ? d.toLocaleDateString("en-US", { weekday: "long", month: "short", day: "numeric", year: "numeric" }) : "";
}

// function(formatTime)
/** "10:00 AM" for a value with a time, else "". */
export function formatTime(value: string | undefined): string {
  if (!value || value.length <= 10) return "";
  const d = parse(value);
  return d ? time(d) : "";
}

/** A note's signed time: with the time when the value carries one. */
export function formatNoteWhen(value: string): string {
  return formatWhen(value, undefined, value.length > 10);
}

// function(sortNewestFirst)
export function sortNewestFirst<T extends { start?: string; date?: string }>(items: T[]): T[] {
  const key = (x: T) => parse(x.start ?? x.date ?? "")?.getTime() ?? 0;
  return [...items].sort((a, b) => key(b) - key(a));
}

// function(nonVisits)
/** Calls, messages, results reviews, referrals, addenda and letters, newest first. */
export function nonVisits(encounters: Encounter[]): Encounter[] {
  return sortNewestFirst(encounters.filter((e) => e.group === "non_visit"));
}

// function(noteIndex)
export interface NoteIndex {
  byEncounter: Map<string, Note[]>;
  byId: Map<string, Note>;
  /** Each note's place in the list, so a visit's notes keep the list order. */
  order: Map<string, number>;
}

// One index per notes array: the screens pass the same array everywhere, so it is built once.
const indexes = new WeakMap<Note[], NoteIndex>();

/**
 * Notes by their visit and by id. Looking up a visit's notes is then a map
 * read instead of a pass over every note (the timeline does it per day, per filter).
 *
 * @param notes - All notes.
 * @returns The index, cached for this array.
 */
export function noteIndex(notes: Note[]): NoteIndex {
  const cached = indexes.get(notes);
  if (cached) return cached;
  const index: NoteIndex = { byEncounter: new Map(), byId: new Map(), order: new Map() };
  notes.forEach((n, i) => {
    index.byId.set(n.id, n);
    index.order.set(n.id, i);
    if (n.encounterId !== null) {
      const list = index.byEncounter.get(n.encounterId);
      if (list) list.push(n); else index.byEncounter.set(n.encounterId, [n]);
    }
  });
  indexes.set(notes, index);
  return index;
}

/**
 * The notes of these visits (by the note's own link or a visit's list), each
 * once, in the order of `notes`.
 */
export function notesOfEncounters(encounters: Pick<Encounter, "id" | "noteIds">[], notes: Note[]): Note[] {
  const index = noteIndex(notes);
  const found = new Map<string, Note>();
  for (const e of encounters) {
    for (const n of index.byEncounter.get(e.id) ?? []) found.set(n.id, n);
    for (const id of e.noteIds) {
      const n = index.byId.get(id);
      if (n) found.set(n.id, n);
    }
  }
  return [...found.values()].sort((a, b) => (index.order.get(a.id) ?? 0) - (index.order.get(b.id) ?? 0));
}

// function(notesForVisit)
/** The notes linked to this visit (by the note's own link or the visit's list), newest first. */
export function notesForVisit(encounter: Encounter, notes: Note[]): Note[] {
  return sortNewestFirst(notesOfEncounters([encounter], notes));
}

// function(isOrderNote)
/**
 * An order and its result from a chart print ("Orders and results"): shown in
 * its own group under a visit's notes so it doesn't bury the clinical notes.
 */
export function isOrderNote(note: Note): boolean {
  return note.category === "report" && (note.typeDisplay === "Orders and results" || note.typeRaw === "orders");
}

// function(noteChips)
/** Note types for a visit card, each once: at most `max`, then "+N" (never "Clinical note · Clinical note"). */
export function noteChips(visitNotes: Note[], max = 2): string[] {
  const labels = [...new Set(visitNotes.map(noteTypeLabel))];
  if (labels.length <= max) return labels;
  return [...labels.slice(0, max), `+${labels.length - max}`];
}

// function(isInferredVisit)
/** A visit whose notes were linked by date and clinician rather than by the source itself. */
export function isInferredVisit(visitNotes: Note[]): boolean {
  return visitNotes.some((n) => n.linkConfidence !== "high");
}

// function(notesToReview)
/** Notes we could not place but have a likely visit for: the "To review" tab. */
export function notesToReview(notes: Note[]): Note[] {
  return sortNewestFirst(notes.filter((n) => n.encounterId === null && Boolean(n.suggestedEncounterId)));
}

// function(notesWithoutVisit)
/**
 * Doctors' notes we found no visit for, not even one on their date, that the
 * patient hasn't kept unlinked. Shown in To review to read, never asked
 * about and never counted: the patient can't say which visit they're from
 * (2026-10-29). Results and orders are left out (they don't need one).
 */
export function notesWithoutVisit(notes: Note[]): Note[] {
  return sortNewestFirst(notes.filter((n) => n.encounterId === null && !n.suggestedEncounterId && !n.keptUnlinked
    && n.category !== "report" && !isOrderNote(n)));
}

// function(summaryCounts)
export function summaryCounts(encounters: Encounter[], notes: Note[]) {
  return {
    visits: encounters.filter((e) => e.group === "visit").length,
    clinicalNotes: notes.filter((n) => n.category !== "report").length,
    reports: notes.filter((n) => n.category === "report").length,
    callsAndMessages: encounters.filter((e) => e.group === "non_visit").length,
    // Only a visit on the note's own day to confirm or pick (notesWithoutVisit are never asked).
    toReview: notesToReview(notes).length,
  };
}

// function(searchTerms)
/** The words typed, for highlighting in the note. */
export function searchTerms(q: string): string[] {
  return q.split(/\s+/).map((w) => w.replace(/^[^\p{L}\p{N}]+|[^\p{L}\p{N}]+$/gu, "")).filter((w) => w.length > 0);
}
